import { useSelector } from 'react-redux';
import { betreuerByNrSelector, skontodefinitionenByArtSelector } from 'store/lookups/selector';
import { useAuftragList } from 'api/auftrag';
import * as React from 'react';
import { useBetreuerprovisionListByNr } from 'api/betreuerprovision';
import { AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { AuftragSummary, defaultLineData, FilterValues, LineData } from '../types';
import { getBetreuerAnteil } from '../utils';

export interface AuftragSummaryProps extends FilterValues {
    enabled: boolean;
}

export const useAuftragSummary = ({
    statusList,
    datumVon,
    datumBis,
    kNr,
    konzernNr,
    kundePlzVon,
    kundePlzBis,
    kundeUmsatzTsdVon,
    kundeUmsatzTsdBis,
    kundeBranchen,
    kundeErweiterteSuchen,
    kundeInteresseFuerProdukte,
    kundeBetreuer,
    kundeTelBetreuer,
    betreuer,
    betreuerGebiet,
    telBetreuer,
    telBetreuerGebiet,
    produktgruppen,
}: AuftragSummaryProps): AuftragSummary => {
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const betreuerprovisionListByNr = useBetreuerprovisionListByNr();
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const auftragList = useAuftragList(
        {
            statusList,
            datumVon,
            datumBis,
            kNr,
            konzernNr,
            kundePlzVon,
            kundePlzBis,
            kundeUmsatzVon: kundeUmsatzTsdVon ? kundeUmsatzTsdVon * 1000 : undefined,
            kundeUmsatzBis: kundeUmsatzTsdBis ? kundeUmsatzTsdBis * 1000 : undefined,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundeBetreuerExact: kundeBetreuer,
            kundeTelBetreuerExact: kundeTelBetreuer,
            betreuerExact: betreuer,
            telBetreuerExact: telBetreuer,
            produktgruppen,
            rahmen: false,
            withKunde: true,
        },
        {},
    );

    const auftragPositionFilter = React.useMemo<AuftragPositionFilter>(
        () => ({ auNrList: auftragList.data?.map((au) => au.nr) || [], withArtikelDetail: true }),
        [auftragList],
    );
    const auftragPositionList = useAuftragPositionList(auftragPositionFilter, { enabled: !!auftragList.data });

    return React.useMemo<AuftragSummary>(() => {
        const total: LineData = { ...defaultLineData };
        const betreuerTotal: {
            [key: number]: LineData;
        } = {};
        const telBetreuerTotal: {
            [key: number]: LineData;
        } = {};

        const auftragLineDataList: LineData[] = [];

        const usedProduktgruppen: Record<string, number> = {};

        (auftragList.data || [])
            .sort((a, b) => (a.datum + a.nr).localeCompare(b.datum + b.nr))
            .forEach((auftrag) => {
                /*
                if (![1, 9, 10, 4, 0].includes(auftrag.status) || auftrag.rahmen) {
                    return;
                }
                 */

                if (auftrag.rahmen) {
                    return;
                }

                if (betreuer && betreuerGebiet && auftrag.kunde?.betreuer !== betreuer) {
                    return;
                }

                if (telBetreuer && telBetreuerGebiet && auftrag.kunde?.telBetreuer !== telBetreuer) {
                    return;
                }

                const filteredAuftragPositionList = (auftragPositionList.data || []).filter(
                    (ap) => ap.auNr === auftrag.nr && ap.lArt !== 'X',
                );

                if (filteredAuftragPositionList.length === 0) {
                    return;
                }

                const auftragLineData: LineData = {
                    ...defaultLineData,
                    auNr: auftrag.nr + (auftrag.betrUm ? '*' : ''),
                    datum: auftrag.datum,
                    kNr: auftrag.kNr || 0,
                    kName: auftrag.kunde?.name1 || '',
                };

                const skontoP = skontodefinitionenByArt[auftrag.zahlBed]?.skP1 || 0;
                // const provision = betreuerByNr[auftrag.betreuer]?.provision || 0;

                let ekNetto = 0;
                let vkNetto = 0;

                filteredAuftragPositionList.forEach((auftragPosition) => {
                    ekNetto += auftragPosition.posEkPreis * auftragPosition.posMenge;
                    vkNetto += auftragPosition.posEPreis * auftragPosition.posMenge;

                    if (auftragPosition.artikelDetail?.produktGruppeNr) {
                        if (!usedProduktgruppen[auftragPosition.artikelDetail?.produktGruppeNr]) {
                            usedProduktgruppen[auftragPosition.artikelDetail?.produktGruppeNr] = 0;
                        }
                        usedProduktgruppen[auftragPosition.artikelDetail?.produktGruppeNr] += 1;
                    }
                });

                const tipp = auftrag.tipp === 0 ? 0 : (vkNetto / 100) * auftrag.tipp;
                const skonto = skontoP === 0 ? 0 : (vkNetto / 100) * skontoP;
                const bMarge = vkNetto - ekNetto;
                const nMarge = bMarge - tipp - skonto - auftrag.fracht - auftrag.roadPr;

                // const bAnteil = (nMarge / 100) * provision;
                const { value: bAnteil } = getBetreuerAnteil(
                    betreuerprovisionListByNr,
                    auftrag.betreuer,
                    auftrag.datum,
                    (betreuerTotal[auftrag.betreuer]?.nMarge || 0) + nMarge,
                    betreuerTotal[auftrag.betreuer]?.bAnteil || 0,
                );
                const fAnteil = nMarge - bAnteil;

                total.count += 1;

                total.ekNetto += ekNetto;
                auftragLineData.ekNetto = ekNetto;

                total.vkNetto += vkNetto;
                auftragLineData.vkNetto = vkNetto;

                total.bMarge += bMarge;
                auftragLineData.bMarge = bMarge;

                total.tippProv += tipp;
                auftragLineData.tippProv = tipp;

                total.skonto += skonto;
                auftragLineData.skonto = skonto;

                total.fracht += auftrag.fracht + auftrag.roadPr;
                auftragLineData.fracht = auftrag.fracht + auftrag.roadPr;

                total.nMarge += nMarge;
                auftragLineData.nMarge = nMarge;

                total.bAnteil += bAnteil;
                auftragLineData.bAnteil = bAnteil;

                total.fAnteil += fAnteil;
                auftragLineData.fAnteil = fAnteil;

                if (auftrag.betrUm) {
                    total.betrUmCount += 1;
                    total.betrUm += vkNetto;
                }

                if (auftrag.betreuer) {
                    if (!betreuerTotal[auftrag.betreuer]) {
                        betreuerTotal[auftrag.betreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[auftrag.betreuer]?.name || betreuerByNr[auftrag.betreuer]?.kz,
                        };
                    }

                    betreuerTotal[auftrag.betreuer].betreuerNr = auftrag.betreuer;
                    betreuerTotal[auftrag.betreuer].count += 1;
                    betreuerTotal[auftrag.betreuer].ekNetto += ekNetto;
                    betreuerTotal[auftrag.betreuer].vkNetto += vkNetto;
                    betreuerTotal[auftrag.betreuer].bMarge += bMarge;
                    betreuerTotal[auftrag.betreuer].tippProv += tipp;
                    betreuerTotal[auftrag.betreuer].skonto += skonto;
                    betreuerTotal[auftrag.betreuer].fracht += auftrag.fracht + auftrag.roadPr;
                    betreuerTotal[auftrag.betreuer].nMarge += nMarge;
                    betreuerTotal[auftrag.betreuer].bAnteil += bAnteil;
                    betreuerTotal[auftrag.betreuer].fAnteil += fAnteil;
                }

                if (auftrag.telBetreuer) {
                    if (!telBetreuerTotal[auftrag.telBetreuer]) {
                        telBetreuerTotal[auftrag.telBetreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[auftrag.telBetreuer]?.name,
                        };
                    }

                    telBetreuerTotal[auftrag.telBetreuer].betreuerNr = auftrag.telBetreuer;
                    telBetreuerTotal[auftrag.telBetreuer].count += 1;
                    telBetreuerTotal[auftrag.telBetreuer].ekNetto += ekNetto;
                    telBetreuerTotal[auftrag.telBetreuer].vkNetto += vkNetto;
                    telBetreuerTotal[auftrag.telBetreuer].bMarge += bMarge;
                    telBetreuerTotal[auftrag.telBetreuer].tippProv += tipp;
                    telBetreuerTotal[auftrag.telBetreuer].skonto += skonto;
                    telBetreuerTotal[auftrag.telBetreuer].fracht += auftrag.fracht + auftrag.roadPr;
                    telBetreuerTotal[auftrag.telBetreuer].nMarge += nMarge;
                    // telBetreuerTotal[auftrag.telBetreuer].bAnteil += bAnteil;
                    telBetreuerTotal[auftrag.telBetreuer].fAnteil += fAnteil;
                }

                auftragLineDataList.push(auftragLineData);
            });

        let fAnteilTotal = 0;
        Object.values(betreuerTotal).forEach((item) => {
            const provision = betreuerByNr[item.betreuerNr]?.provision || 0;
            if (provision >= 100) {
                fAnteilTotal += item.bAnteil;
            } else {
                fAnteilTotal += item.fAnteil;
            }
        });

        return {
            loading:
                auftragList.isLoading ||
                auftragList.isFetching ||
                auftragPositionList.isLoading ||
                auftragPositionList.isFetching,
            count: total.count,
            total,
            betreuerTotal,
            telBetreuerTotal,
            auftragLineDataList,
            fAnteilTotal,
            usedProduktgruppen,
        };
    }, [
        betreuerprovisionListByNr,
        auftragList.isLoading,
        auftragList.isFetching,
        auftragPositionList.isLoading,
        auftragPositionList.isFetching,
        auftragList.data,
        auftragPositionList.data,
        skontodefinitionenByArt,
        betreuerByNr,
        betreuer,
        betreuerGebiet,
        telBetreuer,
        telBetreuerGebiet,
    ]);
};
